'use client'

import { useCallback, useEffect, useState } from 'react'

import type { PortalTutorialTargetRect } from '../interfaces/portal-tutorial-target-rect.interface'
import { getPortalTutorialMeasurableElement } from '../utils/getPortalTutorialMeasurableElement'

export function usePortalTutorialTargetRect(targetId: string | null, enabled: boolean) {
  const [rect, setRect] = useState<PortalTutorialTargetRect | null>(null)

  const measureTarget = useCallback(() => {
    if (!targetId || typeof document === 'undefined') {
      setRect(null)
      return
    }

    const element = getPortalTutorialMeasurableElement(
      document.querySelector<HTMLElement>(`[data-portal-tutorial="${targetId}"]`)
    )

    if (!element) {
      setRect(null)
      return
    }

    const bounds = element.getBoundingClientRect()
    setRect({
      top: bounds.top,
      left: bounds.left,
      width: bounds.width,
      height: bounds.height,
    })
  }, [targetId])

  useEffect(() => {
    if (!enabled) {
      setRect(null)
      return
    }

    measureTarget()

    const handleLayoutChange = () => measureTarget()

    window.addEventListener('resize', handleLayoutChange)
    window.addEventListener('scroll', handleLayoutChange, true)

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            measureTarget()
          })
        : null

    const element = targetId
      ? getPortalTutorialMeasurableElement(document.querySelector<HTMLElement>(`[data-portal-tutorial="${targetId}"]`))
      : null

    if (element && resizeObserver) {
      resizeObserver.observe(element)
    }

    return () => {
      window.removeEventListener('resize', handleLayoutChange)
      window.removeEventListener('scroll', handleLayoutChange, true)
      resizeObserver?.disconnect()
    }
  }, [enabled, measureTarget, targetId])

  return rect
}
